// The three one-time follower packages, in one place.
//
// Both the public pricing page and the /50off funnel render these, and the
// backend prices them from its own table (routes/purchase.js ONE_TIME_PRICES) —
// these numbers are for display only and must match it. Duplicating them per
// page is how a page ends up advertising a price the checkout won't honour.
//
// `key` is the plan key the API expects; the customer-facing names live in the
// language dictionaries.
// `rangeLow` is the bottom of the delivery estimate the visualizer projects;
// `followers` is the top of it.
export const PACKAGES = [
	{ key: 'starter', followers: 200, rangeLow: 100, price: 2.99, anchor: 5.99 },
	{ key: 'basic', followers: 1200, rangeLow: 600, price: 9.99, anchor: 19.99, popular: true },
	{ key: 'pro', followers: 4000, rangeLow: 2000, price: 24.99, anchor: 49.99, bestValue: true },
];

// Quality tiers, chosen after the quantity. The multiplier applies to whatever
// the chosen quantity costs, so this works for a package price or a dashboard
// slider figure alike. Mirrored by QUALITY_TIERS in routes/purchase.js, which
// is the authority on what is charged.
//
// `key` is what the API expects; the customer-facing names live in the language
// dictionaries alongside each tier's feature list.
export const QUALITY_TIERS = [
	{ key: 'high', multiplier: 1 },
	{ key: 'active', multiplier: 2, popular: true },
	{ key: 'vip', multiplier: 3 },
];

export const DEFAULT_QUALITY = 'high';

// Prices end in .99 at every tier: 2 × $9.99 is $19.98, which reads as an
// arithmetic result rather than a price. Rounding to the next .99 puts it at
// $19.99 — a cent more, and recognisably a price.
export const to99 = (n) => Math.floor(n) + 0.99;

export const qualityPrice = (base, tierKey) => {
	const tier = QUALITY_TIERS.find((t) => t.key === tierKey) || QUALITY_TIERS[0];
	return tier.multiplier === 1 ? base : to99(base * tier.multiplier);
};

// Paid add-ons, priced against the package they attach to instead of one flat
// figure: $4.99 was half the price of the 200 pack and a fifth of the 4,000.
// Taking all three is a bundle, cheaper than the sum — that is the `all` price,
// and it is why this cannot be a per-key lookup.
//
// Mirrored by ADDON_PRICES in routes/purchase.js, which is what charges.
export const ADDON_KEYS = ['fast', 'usa', 'female'];
export const ADDON_PRICES = {
	starter: { fast: 1.49, usa: 1.79, female: 1.79, all: 3.99 },
	basic:   { fast: 4.99, usa: 5.99, female: 5.99, all: 12.99 },
	pro:     { fast: 12.49, usa: 14.99, female: 14.99, all: 32.99 },
};

export const addonTotalFor = (planKey, chosen = []) => {
	const table = ADDON_PRICES[planKey] || ADDON_PRICES[DEFAULT_PACKAGE];
	const picked = ADDON_KEYS.filter((k) => chosen.includes(k));
	if (picked.length === ADDON_KEYS.length) return table.all;
	return Math.round(picked.reduce((sum, k) => sum + table[k], 0) * 100) / 100;
};

export const addonPriceFor = (planKey, key) =>
	(ADDON_PRICES[planKey] || ADDON_PRICES[DEFAULT_PACKAGE])[key] ?? 0;

// Core is the default selection everywhere it is offered.
export const DEFAULT_PACKAGE = 'basic';

export const packageFor = (key) => PACKAGES.find((p) => p.key === key) || PACKAGES.find((p) => p.key === DEFAULT_PACKAGE);

// Whole dollars stay bare ($29); anything with cents shows both ($9.99).
export const money = (n) => {
	const cents = Math.round(n * 100);
	return cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2);
};
